import { getStoredSessionToken } from "./appleAuth";
import { chunkDateRange, SCRAPE_DAYS_PER_INVOKE } from "./scrapeLimits";

export type ScrapeDayResult = {
  dateKey: string;
  ok: boolean;
  packRecordName?: string;
  dayRecordName?: string;
  skippedPublished?: boolean;
  sectionCount?: number;
  title?: string;
  season?: string;
  color?: string;
  rank?: string;
  error?: string;
};

export type ScrapeResult = {
  jobId: string;
  source: "usccb" | "awitatpapuri";
  languageCode: "en" | "tl";
  dateKeyStart: string;
  dateKeyEnd: string;
  status: "succeeded" | "failed";
  packsCreated: number;
  packsSkippedPublished: number;
  daysAttempted: number;
  daysFailed: number;
  results: ScrapeDayResult[];
  errorSummary: string | null;
  /** Present when the UI merged multiple free-tier chunks. */
  chunks?: number;
};

/** @deprecated Use ScrapeResult */
export type UsccbScrapeResult = ScrapeResult;

export type ScrapeProgress = {
  chunkIndex: number;
  chunkTotal: number;
  dateKeyStart: string;
  dateKeyEnd: string;
};

async function authHeader(): Promise<HeadersInit> {
  const token = getStoredSessionToken();
  if (!token) throw new Error("Sign in required.");
  return { Authorization: `Bearer ${token}` };
}

async function runScrapeChunk(
  path: string,
  input: { dateKeyStart: string; dateKeyEnd: string },
): Promise<ScrapeResult> {
  const headers = new Headers(await authHeader());
  headers.set("Content-Type", "application/json");
  const res = await fetch(path, {
    method: "POST",
    headers,
    body: JSON.stringify(input),
  });
  const data = (await res.json()) as ScrapeResult & { error?: string };
  if (!res.ok) throw new Error(data.error ?? `Scrape failed (${res.status})`);
  return data;
}

async function runScrapeChunked(
  path: string,
  input: { dateKeyStart: string; dateKeyEnd: string },
  onProgress?: (progress: ScrapeProgress) => void,
): Promise<ScrapeResult> {
  const chunks = chunkDateRange(input.dateKeyStart, input.dateKeyEnd, SCRAPE_DAYS_PER_INVOKE);
  const merged: ScrapeResult = {
    jobId: "",
    source: path.includes("awitatpapuri") ? "awitatpapuri" : "usccb",
    languageCode: path.includes("awitatpapuri") ? "tl" : "en",
    dateKeyStart: input.dateKeyStart,
    dateKeyEnd: input.dateKeyEnd,
    status: "succeeded",
    packsCreated: 0,
    packsSkippedPublished: 0,
    daysAttempted: 0,
    daysFailed: 0,
    results: [],
    errorSummary: null,
    chunks: chunks.length,
  };

  for (let i = 0; i < chunks.length; i++) {
    const chunk = chunks[i]!;
    onProgress?.({
      chunkIndex: i + 1,
      chunkTotal: chunks.length,
      dateKeyStart: chunk.dateKeyStart,
      dateKeyEnd: chunk.dateKeyEnd,
    });
    const part = await runScrapeChunk(path, chunk);
    if (!merged.jobId) merged.jobId = part.jobId;
    else merged.jobId = `${merged.jobId}+${part.jobId}`;
    merged.source = part.source;
    merged.languageCode = part.languageCode;
    merged.packsCreated += part.packsCreated;
    merged.packsSkippedPublished += part.packsSkippedPublished;
    merged.daysAttempted += part.daysAttempted;
    merged.daysFailed += part.daysFailed;
    merged.results.push(...part.results);
  }

  merged.status =
    merged.daysFailed === 0
      ? "succeeded"
      : merged.daysFailed === merged.daysAttempted
        ? "failed"
        : "succeeded";
  merged.errorSummary =
    merged.daysFailed > 0
      ? `${merged.daysFailed} of ${merged.daysAttempted} day(s) failed across ${chunks.length} chunk(s).`
      : null;

  return merged;
}

export async function runUsccbScrape(
  input: { dateKeyStart: string; dateKeyEnd: string },
  onProgress?: (progress: ScrapeProgress) => void,
): Promise<ScrapeResult> {
  return runScrapeChunked("/api/scrape/usccb", input, onProgress);
}

export async function runAwitatpapuriScrape(
  input: { dateKeyStart: string; dateKeyEnd: string },
  onProgress?: (progress: ScrapeProgress) => void,
): Promise<ScrapeResult> {
  return runScrapeChunked("/api/scrape/awitatpapuri", input, onProgress);
}

export { SCRAPE_DAYS_PER_INVOKE };
